import React, { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Handshake, Siren, Zap, ListChecks, ArrowRight, BellRing, CheckCircle2, FileText, ThumbsUp, Wallet, Lock } from "lucide-react";
import { api } from "@/lib/api";

const PERKS = [
  { i: Siren, t: "Emergência atendida", d: "Equipe sempre pronta para vazamentos, curtos e quebras." },
  { i: Zap, t: "Executa antes, paga depois", d: "Sem esperar verba para resolver o que é urgente." },
  { i: ListChecks, t: "Lista contínua", d: "Anote os serviços e acompanhe tudo pelo app." },
];
const STEPS = [
  { i: FileText, t: "Solicite" }, { i: ThumbsUp, t: "Aprove" }, { i: Wallet, t: "Pague" }, { i: Handshake, t: "Desbloqueie o FDP" },
];

const Perks = () => (
  <div className="grid grid-cols-3 gap-2 mt-4">
    {PERKS.map(({ i: I, t, d }) => (
      <div key={t} className="bg-white/10 backdrop-blur rounded-xl p-2.5 sm:p-3 border border-white/15">
        <I className="w-5 h-5 text-amber-300" />
        <p className="font-bold text-xs sm:text-sm mt-1.5 leading-tight">{t}</p>
        <p className="hidden sm:block text-[11px] text-white/80 leading-snug">{d}</p>
      </div>
    ))}
  </div>
);

const Eligible = ({ fdp, onSent }) => {
  const [busy, setBusy] = useState(false);
  const sent = !!fdp.interest_sent_at;
  const interest = async () => {
    setBusy(true);
    try { const r = await api.post("/school/fdp-interest"); onSent?.(r.data.interest_sent_at); toast.success("Pronto! A AMC vai entrar em contato com você."); }
    catch (e) { toast.error(e?.response?.data?.detail || "Erro ao enviar"); }
    finally { setBusy(false); }
  };
  return (
    <>
      <p className="text-[10px] font-bold tracking-widest uppercase text-amber-300">Você foi selecionada</p>
      <h3 className="font-heading text-xl sm:text-2xl font-black leading-tight mt-1">Sua escola já pode entrar no Faz Depois Paga</h3>
      <p className="text-sm text-white/85 mt-1">Como cliente AMC com serviço pago, você tem acesso ao sistema FDP: manutenção sem burocracia, na hora que a escola precisa.</p>
      <Perks />
      <div className="flex flex-wrap gap-2 mt-4">
        <Link to="/faz-depois-paga" target="_blank" data-testid="fdp-promo-know" className="h-10 px-4 rounded-full bg-white text-violet-700 font-bold text-sm inline-flex items-center gap-1.5 hover:bg-amber-100 transition-colors">
          Conhecer o FDP <ArrowRight className="w-4 h-4" />
        </Link>
        <button type="button" onClick={interest} disabled={busy || sent} data-testid="fdp-promo-interest"
          className="h-10 px-4 rounded-full bg-amber-400 text-slate-900 font-bold text-sm inline-flex items-center gap-1.5 hover:bg-amber-300 disabled:opacity-80 transition-colors">
          {sent ? <><CheckCircle2 className="w-4 h-4" /> Interesse enviado</> : <><BellRing className="w-4 h-4" /> Tenho interesse</>}
        </button>
      </div>
    </>
  );
};

const Locked = ({ inRequest }) => (
  <>
    <p className="text-[10px] font-bold tracking-widest uppercase text-amber-300 inline-flex items-center gap-1"><Lock className="w-3 h-3" /> Benefício exclusivo</p>
    <h3 className="font-heading text-xl sm:text-2xl font-black leading-tight mt-1">Imagine ter uma empresa pronta para qualquer emergência</h3>
    <p className="text-sm text-white/85 mt-1">Faça seu primeiro serviço com a AMC e desbloqueie o <b>Faz Depois Paga</b>: a escola pede, a AMC resolve na hora e o pagamento vem depois.</p>
    <div className="flex items-center gap-1 sm:gap-2 mt-4 flex-wrap" data-testid="fdp-promo-steps">
      {STEPS.map(({ i: I, t }, idx) => (
        <React.Fragment key={t}>
          <span className={`inline-flex items-center gap-1.5 h-8 px-3 rounded-full text-xs font-bold ${idx === 3 ? "bg-amber-400 text-slate-900" : "bg-white/15"}`}><I className="w-3.5 h-3.5" />{t}</span>
          {idx < 3 && <ArrowRight className="w-3.5 h-3.5 text-white/60" />}
        </React.Fragment>
      ))}
    </div>
    <Perks />
    {inRequest ? (
      <p className="text-xs text-white/80 mt-3">Comece agora: descreva abaixo o serviço que sua escola precisa.</p>
    ) : (
      <Link to="/escola/solicitar" data-testid="fdp-promo-request" className="mt-4 h-10 px-4 rounded-full bg-amber-400 text-slate-900 font-bold text-sm inline-flex items-center gap-1.5 hover:bg-amber-300 transition-colors">
        Solicitar meu primeiro serviço <ArrowRight className="w-4 h-4" />
      </Link>
    )}
  </>
);

export const FdpPromoCard = ({ fdp, onSent, inRequest = false }) => {
  if (!fdp || fdp.fdp_active) return null;
  return (
    <div className="relative overflow-hidden rounded-2xl p-5 text-white bg-gradient-to-br from-violet-700 via-violet-600 to-fuchsia-600 shadow-lg"
      data-testid={fdp.eligible ? "fdp-promo-eligible" : "fdp-promo-locked"}>
      <Handshake className="absolute -right-6 -bottom-6 w-40 h-40 text-white/10 rotate-12 pointer-events-none" />
      <div className="relative">{fdp.eligible ? <Eligible fdp={fdp} onSent={onSent} /> : <Locked inRequest={inRequest} />}</div>
    </div>
  );
};
